import React, { useMemo, useState } from 'react';
import { X, Search, Check, Minus, Plus } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { Input } from '../SubscriptionAppInput';
export interface BulkTagModalTag {
  id: string;
  label: string;
  color: 'blue' | 'green' | 'orange' | 'purple' | 'gray';
  appliedToCount?: number; // How many of the selected contacts have this tag
}
export interface BulkTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCount: number;
  availableTags: BulkTagModalTag[];
  onApply: (tagsToAdd: string[], tagsToRemove: string[], newTagsWithColors?: Map<string, {
    label: string;
    color: string;
  }>) => void;
}
export function BulkTagModal({
  isOpen,
  onClose,
  selectedCount,
  availableTags,
  onApply
}: BulkTagModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [deselectedTags, setDeselectedTags] = useState<Set<string>>(new Set());
  const [newTagColor, setNewTagColor] = useState<'blue' | 'green' | 'orange' | 'purple' | 'gray'>('blue');
  const [newCustomColor, setNewCustomColor] = useState('#0EA5E9');
  // Store newly created tags with their colors
  const [newlyCreatedTags, setNewlyCreatedTags] = useState<Map<string, {
    label: string;
    color: string;
  }>>(new Map());
  // Color options
  const colorOptions: Array<{
    value: 'blue' | 'green' | 'orange' | 'purple' | 'gray';
    label: string;
    preview: string;
  }> = [{
    value: 'blue',
    label: 'Blue',
    preview: 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]'
  }, {
    value: 'green',
    label: 'Green',
    preview: 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]'
  }, {
    value: 'orange',
    label: 'Orange',
    preview: 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]'
  }, {
    value: 'purple',
    label: 'Purple',
    preview: 'bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]'
  }, {
    value: 'gray',
    label: 'Gray',
    preview: 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]'
  }];
  // Filter tags based on search
  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) return availableTags;
    const query = searchQuery.toLowerCase();
    return availableTags.filter(tag => tag.label.toLowerCase().includes(query));
  }, [availableTags, searchQuery]);
  // Check if search query would create a new tag
  const exactMatch = availableTags.find(tag => tag.label.toLowerCase() === searchQuery.toLowerCase());
  const canCreateNew = searchQuery.trim() && !exactMatch;
  // Get tag state (all, some, none)
  const getTagState = (tag: BulkTagModalTag): 'all' | 'some' | 'none' => {
    if (selectedTags.has(tag.id)) return 'all';
    if (deselectedTags.has(tag.id)) return 'none';
    if (!tag.appliedToCount) return 'none';
    if (tag.appliedToCount === selectedCount) return 'all';
    return 'some';
  };
  // Handle tag click
  const handleTagClick = (tag: BulkTagModalTag) => {
    const currentState = getTagState(tag);
    const newSelected = new Set(selectedTags);
    const newDeselected = new Set(deselectedTags);
    if (currentState === 'none') {
      // None -> All (add to all)
      newSelected.add(tag.id);
      newDeselected.delete(tag.id);
    } else if (currentState === 'some') {
      // Some -> All (add to all)
      newSelected.add(tag.id);
      newDeselected.delete(tag.id);
    } else {
      // All -> None (remove from all)
      newSelected.delete(tag.id);
      newDeselected.add(tag.id);
    }
    setSelectedTags(newSelected);
    setDeselectedTags(newDeselected);
  };
  // Handle create new tag
  const handleCreateTag = () => {
    if (!canCreateNew) return;
    const newTagId = searchQuery.toLowerCase().replace(/\s+/g, '-');
    const newSelected = new Set(selectedTags);
    newSelected.add(newTagId);
    setSelectedTags(newSelected);
    // Store the newly created tag with its color
    const newTags = new Map(newlyCreatedTags);
    newTags.set(newTagId, {
      label: searchQuery,
      color: newTagColor
    });
    setNewlyCreatedTags(newTags);
    setSearchQuery('');
    setNewTagColor('blue');
  };
  // Calculate changes
  const tagsToAdd = Array.from(selectedTags);
  const tagsToRemove = Array.from(deselectedTags);
  const hasChanges = tagsToAdd.length > 0 || tagsToRemove.length > 0;
  // Handle apply
  const handleApply = () => {
    onApply(tagsToAdd, tagsToRemove, newlyCreatedTags);
    onClose();
  };
  // Get color classes for tags
  const getColorClasses = (color: string, state: 'all' | 'some' | 'none') => {
    const colorMap = {
      blue: {
        all: 'bg-[#0EA5E9] text-white border-[#0EA5E9]',
        some: 'bg-white text-[#0EA5E9] border-[#0EA5E9]',
        none: 'bg-white text-[#374151] border-[#CBD5E1]'
      },
      green: {
        all: 'bg-[#10B981] text-white border-[#10B981]',
        some: 'bg-white text-[#10B981] border-[#10B981]',
        none: 'bg-white text-[#374151] border-[#CBD5E1]'
      },
      orange: {
        all: 'bg-[#F59E0B] text-white border-[#F59E0B]',
        some: 'bg-white text-[#F59E0B] border-[#F59E0B]',
        none: 'bg-white text-[#374151] border-[#CBD5E1]'
      },
      purple: {
        all: 'bg-[#6F42C1] text-white border-[#6F42C1]',
        some: 'bg-white text-[#6F42C1] border-[#6F42C1]',
        none: 'bg-white text-[#374151] border-[#CBD5E1]'
      },
      gray: {
        all: 'bg-[#475569] text-white border-[#475569]',
        some: 'bg-white text-[#475569] border-[#475569]',
        none: 'bg-white text-[#374151] border-[#CBD5E1]'
      }
    };
    return colorMap[color as keyof typeof colorMap]?.[state] || colorMap.blue[state];
  };
  // Get color classes for newly created tags in preview
  const getPreviewColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]',
      green: 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]',
      orange: 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]',
      purple: 'bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]',
      gray: 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]'
    };
    // If it's a custom hex color, return empty (will use inline styles)
    if (color.startsWith('#')) {
      return '';
    }
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };
  // Get inline styles for custom colors in preview
  const getPreviewCustomColorStyles = (color: string) => {
    if (!color.startsWith('#')) return {};
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    const rgb = hexToRgb(color);
    if (!rgb) return {};
    return {
      backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
      color: color,
      borderColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`
    };
  };
  // Early return AFTER all hooks
  if (!isOpen) return null;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-[#0F172A] bg-opacity-40 z-[1040]" onClick={onClose} data-id="element-747" data-mp="1nvqt9ion2yed" />

      {/* Modal */}
      <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4" data-id="element-748" data-mp="1ex6jj7123z2a">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-[640px] max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-749" data-mp="1ez9fs8ssyofg">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-[#E2E8F0]" data-id="element-750" data-mp="14zse02go8uor">
            <div data-id="element-751" data-mp="1077kz35lalma">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-752" data-mp="106pfrh0y8x28">
                Add Tags
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-753" data-mp="w4ksr150ayok">
                Apply tags to {selectedCount}{' '}
                {selectedCount === 1 ? 'contact' : 'contacts'}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors -mr-2 -mt-2" data-id="element-754" data-mp="mac0mejw17qx">
              <X className="w-5 h-5 text-[#64748B]" data-id="element-755" data-mp="263vdmz7lzu1b" />
            </button>
          </div>

          {/* Body - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-756" data-mp="2a45sz2hajwsu">
            {/* Search/Create Input */}
            <div data-id="element-757" data-mp="1w4cx6sqwqqe0">
              <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-758" data-mp="1w4wgacsanktm">
                Tag Name
              </label>
              <Input type="search" placeholder="Search or create new tag..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-[18px] h-[18px]" data-id="element-760" data-mp="1r6l0zmj8ci40" />} onKeyDown={e => {
              if (e.key === 'Enter' && canCreateNew) {
                handleCreateTag();
              }
            }} data-id="element-759" data-mp="1mfml2o25lm1a" />

              {/* Autocomplete Dropdown */}
              {searchQuery && <div className="mt-2 bg-white border border-[#CBD5E1] rounded-md shadow-lg max-h-[400px] overflow-y-auto" data-id="element-761" data-mp="1ibn649wiuva1">
                  {filteredTags.length > 0 ? filteredTags.map(tag => <button key={tag.id} onClick={() => {
                handleTagClick(tag);
                setSearchQuery('');
              }} className="w-full px-4 py-2 text-left hover:bg-[#F1F5F9] transition-colors flex items-center gap-2" data-id="element-762" data-mp="18f21vqsbto79">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getColorClasses(tag.color, getTagState(tag))}`} data-id="element-763" data-mp="ysr30lxcjiov">
                          {tag.label}
                        </span>
                      </button>) : canCreateNew ? <div className="p-4" data-id="element-764" data-mp="17qt7jbgagtgw">
                      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-[#E2E8F0]" data-id="element-765" data-mp="13ru5bylxzat5">
                        <Plus className="w-4 h-4 text-[#0EA5E9]" data-id="element-766" data-mp="1hiz0aned7ex5" />
                        <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-767" data-mp="1hkjycmzw1fzr">
                          Create "{searchQuery}"
                        </span>
                      </div>

                      {/* Color Selection in Dropdown */}
                      <div className="space-y-3" data-id="element-768" data-mp="1miv7swm9tohg">
                        <label className="block text-[12px] font-medium text-[#475569]" data-id="element-769" data-mp="3evhr2yol2tvt">
                          Select Color
                        </label>
                        <div className="grid grid-cols-5 gap-2" data-id="element-770" data-mp="3a385tvyeyhzv">
                          {colorOptions.map(option => <button key={option.value} onClick={e => {
                      e.stopPropagation();
                      setNewTagColor(option.value);
                    }} className={`p-2 rounded-md border-2 transition-all ${newTagColor === option.value ? 'border-[#0EA5E9] ring-2 ring-[#0EA5E9] ring-opacity-20' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} title={option.label} data-id="element-771" data-mp="356hrbd29bugk">
                              <span className={`inline-flex items-center justify-center w-full px-2 py-1 rounded text-xs font-medium border ${option.preview}`} data-id="element-772" data-mp="3oscmh8pk8e5v">
                                {option.label.charAt(0)}
                              </span>
                            </button>)}
                        </div>

                        {/* Custom Color in Dropdown */}
                        <div className="flex items-center gap-2 p-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md" data-id="element-773" data-mp="ny6cr2lw0pol">
                          <input type="color" value={newCustomColor} onChange={e => {
                      e.stopPropagation();
                      setNewCustomColor(e.target.value);
                      setNewTagColor(e.target.value as any);
                    }} className="w-8 h-8 rounded border border-[#CBD5E1] cursor-pointer" title="Custom color" data-id="element-774" data-mp="sx9hsn1vavwk" />
                          <input type="text" value={typeof newTagColor === 'string' && newTagColor.startsWith('#') ? newTagColor : newCustomColor} onChange={e => {
                      e.stopPropagation();
                      const value = e.target.value;
                      if (value.startsWith('#')) {
                        setNewCustomColor(value);
                        setNewTagColor(value as any);
                      }
                    }} onClick={e => e.stopPropagation()} placeholder="#0EA5E9" className="flex-1 px-2 py-1 text-[13px] border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9]" data-id="element-775" data-mp="1bmpjvet7awf3" />
                        </div>

                        {/* Create Button */}
                        <button onClick={handleCreateTag} className="w-full px-4 py-2 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7] transition-colors" data-id="element-776" data-mp="12mryw8vzjgdt">
                          Create Tag
                        </button>
                      </div>
                    </div> : <div className="px-4 py-2 text-sm text-[#94A3B8]" data-id="element-777" data-mp="17jifqyfm6g0a">
                      No matching tags
                    </div>}
                </div>}
            </div>

            {/* Available Tags */}
            <div data-id="element-778" data-mp="1hamrlp9ztkqw">
              <label className="block text-[12px] font-semibold text-[#64748B] uppercase tracking-wider mb-3" data-id="element-779" data-mp="1hc7mjg07z5pa">
                Available Tags
              </label>
              {availableTags.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-780" data-mp="1qcma6m0nowvy">
                  {availableTags.map(tag => {
                const state = getTagState(tag);
                return <button key={tag.id} onClick={() => handleTagClick(tag)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[14px] font-medium border transition-all hover:scale-105 ${getColorClasses(tag.color, state)}`} data-id="element-781" data-mp="2evxs75zrhp17">
                        <span data-id="element-782" data-mp="24zkyw984pd6z">{tag.label}</span>
                        {state === 'all' && <Check className="w-4 h-4" data-id="element-783" data-mp="1r5bkjfto66rq" />}
                        {state === 'some' && <Minus className="w-4 h-4" data-id="element-784" data-mp="1vxbn7g14jds0" />}
                      </button>;
              })}
                </div> : <p className="text-[14px] text-[#94A3B8]" data-id="element-785" data-mp="1h8p2szwn3vtx">
                  No tags created yet. Type above to create your first tag.
                </p>}
            </div>

            {/* Changes Preview */}
            {hasChanges && <div data-id="element-786" data-mp="187r6l3sg0slw">
                <label className="block text-[12px] font-semibold text-[#64748B] uppercase tracking-wider mb-3" data-id="element-787" data-mp="189c0qsaui9j8">
                  Changes to apply
                </label>
                <div className="space-y-2" data-id="element-788" data-mp="154bbg8rlqadb">
                  {tagsToAdd.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-789" data-mp="1061fvx1yt7r6">
                      {tagsToAdd.map(tagId => {
                  const tag = availableTags.find(t => t.id === tagId);
                  const newTag = newlyCreatedTags.get(tagId);
                  const displayLabel = tag?.label || newTag?.label || tagId;
                  const displayColor = tag?.color || newTag?.color || 'blue';
                  const colorClasses = getPreviewColorClasses(displayColor);
                  const customStyles = getPreviewCustomColorStyles(displayColor);
                  return <span key={tagId} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[12px] font-medium border ${colorClasses || 'border-[#CBD5E1]'}`} style={colorClasses ? undefined : customStyles} data-id="element-790" data-mp="cj1nh70nt40c">
                            <Plus className="w-3 h-3" data-id="element-791" data-mp="22to4qwsmh2kv" />
                            <span data-id="element-792" data-mp="22u680ddpch2a">{displayLabel}</span>
                            <button onClick={() => {
                      const newSelected = new Set(selectedTags);
                      newSelected.delete(tagId);
                      setSelectedTags(newSelected);
                      // Also remove from newly created tags if it exists
                      if (newlyCreatedTags.has(tagId)) {
                        const newTags = new Map(newlyCreatedTags);
                        newTags.delete(tagId);
                        setNewlyCreatedTags(newTags);
                      }
                    }} className="ml-1 hover:opacity-70 rounded-sm p-0.5 transition-opacity" title="Remove tag" data-id="element-793" data-mp="3krjel4ie3g07">
                              <X className="w-3 h-3" data-id="element-794" data-mp="3auy96i2gk2vu" />
                            </button>
                          </span>;
                })}
                    </div>}
                  {tagsToRemove.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-795" data-mp="q2cxkk1n7yga">
                      {tagsToRemove.map(tagId => {
                  const tag = availableTags.find(t => t.id === tagId);
                  return <span key={tagId} className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-[12px] font-medium bg-[#FEE2E2] text-[#DC2626] border border-[#FECACA]" data-id="element-796" data-mp="2r39bge6mp8n4">
                            <Minus className="w-3 h-3" data-id="element-797" data-mp="311ysy5um4r2u" />
                            <span data-id="element-798" data-mp="2w3xc7rknt3uj">{tag?.label || tagId}</span>
                            <button onClick={() => {
                      const newDeselected = new Set(deselectedTags);
                      newDeselected.delete(tagId);
                      setDeselectedTags(newDeselected);
                    }} className="ml-1 hover:bg-[#FECACA] rounded-sm p-0.5 transition-colors" title="Remove tag" data-id="element-799" data-mp="2w4qcc9p44v3p">
                              <X className="w-3 h-3" data-id="element-800" data-mp="2ddpdrmrwn1ko" />
                            </button>
                          </span>;
                })}
                    </div>}
                </div>
              </div>}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-[#E2E8F0] bg-[#F8FAFC]" data-id="element-801" data-mp="1ko8qt4n20cvu">
            <Button variant="ghost" onClick={onClose} data-id="element-802" data-mp="568xsco08zn">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleApply} disabled={!hasChanges} data-id="element-803" data-mp="4z0r2rcmgfwm">
              Apply Tags
            </Button>
          </div>
        </div>
      </div>
    </>;
}